import React, { Component } from 'react';
import ResultTable from './CapMrCor/ResultTable';
import './CapMrCor/cap-mr-cor.style.scss';

const CAP_TAB_KEY = '__CAP_MR_COR__';

const SHEET_LABEL_MAP = {
    PR_BRANCH_O1: 'Branch',
    PR_PRODUCT_O1: 'Product',
    PR_CUSTOMER_O1: 'Customer',
    PR_UNIT_O1: 'Unit',
    PR_CHANNEL_O1: 'Channel',
    PR_COMMITMENT: 'Commitment',
    PR_LOAN_CONTRACT: 'Loan',
    PR_CARD: 'Card',
    PR_INVESTMENT: 'Investment',
    PR_MM_CONTRACTS: 'MM',
    PR_ICAAP_RESULT: 'ICAAP',
    PR_CAP_COR_BANKING_CUS: 'COR Customer',
};

const STG_CCR_TAB = '__STG_CCR__';

const NUMBER_HINT = /(^n_|^raroc$|^rorwa$|^ep$|rwa|cap|pnl|toi|provision|benefit|amt|bal|value|weight|pct|percentage)/i;
const CODE_HINT = /(code|id|nbr|cif|type|scenario|indicator|date)/i;

const headerLabel = (key) => String(key).replace(/_ytd$/, '');
const fallbackLabel = (key) =>
    String(key)
        .replace(/^PR_/, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (m) => m.toUpperCase());

const formatCell = (key, value, row) => {
    if (value === null || value === undefined || value === '') return '—';
    if (row && row.v_indicators === 'Added percentage' && NUMBER_HINT.test(key) && !CODE_HINT.test(key) && !Number.isNaN(Number(value))) {
        return Number(value).toLocaleString('en-US', {
            style: 'percent',
            maximumFractionDigits: 0,
        });
    }
    if (NUMBER_HINT.test(key) && !CODE_HINT.test(key) && !Number.isNaN(Number(value))) {
        return Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 });
    }
    return String(value);
};

const formatDate = (value) => {
    if (!value) return '—';
    const s = String(value).trim();
    // yyyy-mm-dd → dd/mm/yy
    const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) {
        const d = m[3].padStart(2, '0');
        const mo = m[2].padStart(2, '0');
        const y = m[1].slice(-2);
        return `${d}/${mo}/${y}`;
    }
    return s;
};

function availableSheets(result) {
    const output = (result && result.output) || result;
    if (!output || typeof output !== 'object') return [];

    return Object.keys(output)
        .filter((key) => Array.isArray(output[key]))
        .map((key) => [key, SHEET_LABEL_MAP[key] || fallbackLabel(key)]);
}

function capCount(capResult) {
    if (!capResult) return 0;
    const rows = Array.isArray(capResult.rows) ? capResult.rows.length : 0;
    return rows + 1; // include total row
}

function mergedTabs(result, capResult, stgCcr) {
    const tabs = availableSheets(result);
    const hasStg = stgCcr && stgCcr.dimensions && Object.keys(stgCcr.dimensions).length > 0;
    const list = [];
    if (capResult && capResult.total) {
        list.push([CAP_TAB_KEY, 'RAROC', capCount(capResult)]);
    }
    tabs.forEach(([k, l]) => list.push([k, l, null]));
    if (hasStg) {
        list.push([STG_CCR_TAB, 'STG_CCR', null]);
    }
    return list;
}

function pickColumns(rows) {
    const keys = [];
    rows.slice(0, 20).forEach((row) => {
        Object.keys(row || {}).forEach((key) => {
            if (!keys.includes(key)) keys.push(key);
        });
    });
    return keys;
}

export default class RarocCrResultTable extends Component {
    state = { active: null };

    componentWillReceiveProps(nextProps) {
        const available = mergedTabs(nextProps.result, nextProps.capResult, nextProps.stgCcr);
        if (!available.find(([key]) => key === this.state.active)) {
            this.setState({ active: (available[0] || [])[0] });
        }
    }

    renderStgCcr(stgCcr) {
        if (!stgCcr || !stgCcr.dimensions) return null;
        const dimOrder = ['BRANCH', 'PRODUCT', 'CUSTOMER', 'UNIT', 'CHANNEL'];
        const dimLabel = { BRANCH: 'Branch', PRODUCT: 'Product', CUSTOMER: 'Customer', UNIT: 'Unit', CHANNEL: 'Channel' };
        const columns = ['d_snapshot_date', 'item', 'n_alloc_or_cap_bau', 'n_alloc_or_cap_stress'];
        const colLabels = ['Date', 'Item', 'N_ALLOC_OR_CAP_BAU', 'N_ALLOC_OR_CAP_STRESS'];

        return (
            <div className='cmc'>
                {dimOrder.map((dimKey) => {
                    const rows = stgCcr.dimensions[dimKey] || [];
                    if (!rows.length) return null;
                    return (
                        <div key={dimKey} style={{ marginBottom: 24 }}>
                            <div className='cmc-section-title'>{dimLabel[dimKey] || dimKey}</div>
                            <div className='cmc-table-wrap'>
                                <table className='cmc-table cmc-cr-table'>
                                    <thead>
                                        <tr>
                                            {colLabels.map((h, i) => (
                                                <th key={columns[i]}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, idx) => (
                                            <tr key={idx} className={row.item === 'TOTAL' ? 'cmc-total-row' : ''}>
                                                {columns.map((col, cIdx) => (
                                                    <td key={col} className={cIdx < 2 ? 'cmc-c-item' : ''}>
                                                        {col === 'd_snapshot_date' ? formatDate(row[col]) : formatCell(col, row[col], row)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    );
                })}
                <div className='cmc-cr-note'>
                    Total OR Capital BAU: {formatCell('n_alloc_or_cap_bau', stgCcr.total_or_bau, {})} | Stress: {formatCell('n_alloc_or_cap_stress', stgCcr.total_or_stress, {})}
                </div>
            </div>
        );
    }

    render() {
        const { result, capResult, stgCcr } = this.props;
        const output = (result && result.output) || result;
        const available = mergedTabs(result, capResult, stgCcr);
        if ((!result && !capResult && !stgCcr) || !available.length) return null;

        const active = this.state.active || available[0][0];
        const sheet = [CAP_TAB_KEY, STG_CCR_TAB].includes(active) || (output && output[active]) ? active : available[0][0];

        if (sheet === STG_CCR_TAB) {
            return (
                <div className='cmc'>
                    <div className='cmc-cr-tabs'>
                        {available.map(([key, label]) => (
                            <button key={key} type='button' className={'cmc-seg-btn' + (sheet === key ? ' is-active' : '')} onClick={() => this.setState({ active: key })}>
                                {label}
                            </button>
                        ))}
                    </div>
                    {this.renderStgCcr(stgCcr)}
                </div>
            );
        }

        const rows = sheet === CAP_TAB_KEY ? [] : output[sheet] || [];
        const columns = pickColumns(rows);

        return (
            <div className='cmc'>
                <div className='cmc-meta'>
                    {available.slice(0, 6).map(([key, label, countOverride]) => (
                        <span className='cmc-pill' key={key}>
                            <b>{label}</b> {countOverride != null ? countOverride : (output[key] || []).length}
                        </span>
                    ))}
                </div>

                <div className='cmc-cr-tabs'>
                    {available.map(([key, label]) => (
                        <button key={key} type='button' className={'cmc-seg-btn' + (sheet === key ? ' is-active' : '')} onClick={() => this.setState({ active: key })}>
                            {label}
                        </button>
                    ))}
                </div>

                {sheet === CAP_TAB_KEY ? (
                    <ResultTable result={capResult} showMeta={false} />
                ) : (
                    <div>
                        <div className='cmc-table-wrap'>
                            <table className='cmc-table cmc-cr-table'>
                                <thead>
                                    <tr>
                                        {columns.map((key) => (
                                            <th key={key}>{headerLabel(key)}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.slice(0, 100).map((row, idx) => (
                                        <tr key={idx}>
                                            {columns.map((key, cIdx) => (
                                                <td key={key} className={cIdx < 2 ? 'cmc-c-item' : ''}>
                                                    {formatCell(key, row[key], row)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {rows.length > 100 && <div className='cmc-cr-note'>Showing first 100 rows of {rows.length}.</div>}
                    </div>
                )}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// src/pages/Raroc/CrResultTable.js